import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { Row, Col, Card, Spinner, Badge } from 'react-bootstrap';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { getStats, checkHealth } from '../services/api';

class Dashboard extends Component {
  constructor(props) {
    super(props);
    this.state = {
      stats: null,
      health: null,
      loading: true,
      error: null,
    };
  }

  componentDidMount() {
    this.loadData();
  }

  loadData = async () => {
    try {
      const [stats, health] = await Promise.all([
        getStats(),
        checkHealth(),
      ]);
      this.setState({ stats, health, loading: false });
    } catch (error) {
      this.setState({ 
        error: 'Failed to load dashboard data. Is the API running?',
        loading: false 
      });
    }
  };

  render() {
    const { stats, health, loading, error } = this.state;

    if (loading) {
      return (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Loading dashboard...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="empty-state">
          <div className="empty-state-icon">⚠️</div>
          <h4>{error}</h4>
          <p>Make sure the backend is running: <code>python -m app.api_v2</code></p>
        </div>
      );
    }

    const jobStats = stats?.jobs || {};
    const feedbackStats = stats?.feedback || {};
    const anomalyMethods = stats?.anomalies_by_method || {};

    // Prepare chart data
    const methodData = Object.entries(anomalyMethods).map(([method, count]) => ({
      name: method.replace('_', ' '),
      count,
    }));

    const feedbackPieData = [
      { name: 'True Positives', value: feedbackStats.true_positives || 0 },
      { name: 'False Positives', value: feedbackStats.false_positives || 0 },
    ];

    const COLORS = ['#28a745', '#dc3545'];

    return (
      <div>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2>Dashboard</h2>
          <Badge variant={health?.status === 'healthy' ? 'success' : 'danger'}>
            API: {health?.status || 'unknown'} (v{health?.version})
          </Badge>
        </div>

        {/* Stats Cards */}
        <Row className="mb-4">
          <Col md={3}>
            <Card className="stat-card">
              <div className="stat-value">{jobStats.total_jobs || 0}</div>
              <div className="stat-label">Total Jobs</div>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="stat-card">
              <div className="stat-value">{jobStats.total_rows_processed?.toLocaleString() || 0}</div>
              <div className="stat-label">Rows Processed</div>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="stat-card">
              <div className="stat-value">{jobStats.total_anomalies_detected?.toLocaleString() || 0}</div>
              <div className="stat-label">Anomalies Detected</div>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="stat-card">
              <div className="stat-value">{feedbackStats.total_feedback || 0}</div>
              <div className="stat-label">Feedback Records</div>
            </Card>
          </Col>
        </Row>

        {/* Charts */}
        <Row>
          <Col md={6}>
            <Card>
              <Card.Header>Anomalies by Detection Method</Card.Header>
              <Card.Body>
                {methodData.length > 0 ? (
                  <div className="chart-container">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={methodData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="count" fill="#007bff" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="empty-state">
                    <p>No anomaly data yet</p>
                    <Link to="/upload" className="btn btn-primary">Upload Data</Link>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
          <Col md={6}>
            <Card>
              <Card.Header>Feedback Distribution</Card.Header>
              <Card.Body>
                {feedbackStats.total_feedback > 0 ? (
                  <div className="chart-container">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={feedbackPieData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {feedbackPieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="empty-state">
                    <p>No feedback yet</p>
                    <Link to="/feedback" className="btn btn-primary">Review Anomalies</Link>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Quick Actions */}
        <Row className="mt-4">
          <Col md={12}>
            <Card>
              <Card.Header>Quick Actions</Card.Header>
              <Card.Body>
                <Link to="/upload" className="btn btn-primary mr-2">
                  📤 Upload Data
                </Link>
                <Link to="/jobs" className="btn btn-secondary mr-2">
                  📋 View Jobs
                </Link>
                <Link to="/feedback" className="btn btn-info mr-2">
                  💬 Review Anomalies
                </Link>
                <Link to="/models" className="btn btn-warning">
                  🤖 Manage Models
                </Link>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* System Info */}
        <Row className="mt-4">
          <Col md={6}>
            <Card>
              <Card.Header>Processing Stats</Card.Header>
              <Card.Body>
                <table className="table table-sm">
                  <tbody>
                    <tr>
                      <td>Completed Jobs</td>
                      <td><strong>{jobStats.completed_jobs || 0}</strong></td>
                    </tr>
                    <tr>
                      <td>Failed Jobs</td>
                      <td><strong>{jobStats.failed_jobs || 0}</strong></td>
                    </tr>
                    <tr>
                      <td>Avg Processing Time</td>
                      <td><strong>{jobStats.avg_processing_time_ms || 0} ms</strong></td>
                    </tr>
                  </tbody>
                </table>
              </Card.Body>
            </Card>
          </Col>
          <Col md={6}>
            <Card>
              <Card.Header>Model Performance</Card.Header>
              <Card.Body>
                <table className="table table-sm">
                  <tbody>
                    <tr>
                      <td>Precision</td>
                      <td><strong>{((feedbackStats.precision || 0) * 100).toFixed(1)}%</strong></td>
                    </tr>
                    <tr>
                      <td>True Positives</td>
                      <td><strong>{feedbackStats.true_positives || 0}</strong></td>
                    </tr>
                    <tr>
                      <td>False Positives</td>
                      <td><strong>{feedbackStats.false_positives || 0}</strong></td>
                    </tr>
                  </tbody>
                </table>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    );
  }
}

export default Dashboard;